/**
 * Hook for managing product templates
 * Real-time Firestore listener for templates
 */
import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { ProductTemplate } from '@/types';

export function useTemplates(
  productType?: 'phone_case' | 'coffee_cup',
  includeInactive?: boolean
) {
  const [templates, setTemplates] = useState<ProductTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const constraints: any[] = [
      orderBy('display_order', 'asc')
    ];

    // Only filter by is_active if not including inactive templates
    if (!includeInactive) {
      constraints.unshift(where('is_active', '==', true));
    }

    if (productType) {
      constraints.unshift(where('product_type', '==', productType));
    }

    const q = query(collection(db, 'product_templates'), ...constraints);

    console.log(`📋 [useTemplates] Listening to templates:`, {
      productType: productType || 'all',
      includeInactive: includeInactive || false,
      constraints: constraints.length
    });

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data: ProductTemplate[] = [];
        snapshot.forEach((doc) => {
          const docData = doc.data();

          // Helper to add cache buster to URLs
          const addCacheBuster = (url: string | undefined, cacheBuster: string) => {
            if (!url || !cacheBuster) return url;
            const separator = url.includes('?') ? '&' : '?';
            return `${url}${separator}v=${cacheBuster}`;
          };

          // Calculate cache buster from updated_at
          let cacheBuster = '';
          if (docData.updated_at) {
            const seconds = docData.updated_at.seconds || (typeof docData.updated_at === 'object' ? docData.updated_at.getTime() / 1000 : docData.updated_at / 1000);
            cacheBuster = Math.floor(seconds).toString();
          }

          // Compute urls from CDN fields with cache busters (priority: CDN > Modal)
          // This ensures the latest images are always loaded, not browser cached versions
          let computedUrl = docData.cdn_thumbnail_url || docData.cdn_url;
          if (computedUrl && cacheBuster) {
            computedUrl = addCacheBuster(computedUrl, cacheBuster);
          }

          let computedCleanUrl = docData.cdn_clean_thumbnail_url || docData.cdn_clean_url;
          if (computedCleanUrl && cacheBuster) {
            computedCleanUrl = addCacheBuster(computedCleanUrl, cacheBuster);
          }

          let computedMaskUrl = docData.cdn_mask_url;
          if (computedMaskUrl && cacheBuster) {
            computedMaskUrl = addCacheBuster(computedMaskUrl, cacheBuster);
          }

          // Build template with computed URLs
          const template: ProductTemplate = {
            id: doc.id,
            ...docData,
            // Add computed URLs for components expecting url/cleanUrl/maskUrl
            url: computedUrl,
            cleanUrl: computedCleanUrl,
            maskUrl: computedMaskUrl || 'GENERATE_FALLBACK_MASK'
          } as ProductTemplate;

          data.push(template);
        });
        console.log(`✅ [useTemplates] Received ${data.length} templates:`, data.map(t => ({
          id: t.id,
          name: t.name,
          product_type: t.product_type,
          is_active: t.is_active,
          has_url: !!t.url,
          has_cleanUrl: !!t.cleanUrl,
          has_maskUrl: !!t.maskUrl,
          updated_at: t.updated_at
        })));
        setTemplates(data);
        setLoading(false);
      },
      (err) => {
        console.error('❌ [useTemplates] Error fetching templates:', {
          error: err.message,
          code: (err as any).code
        });
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [productType, includeInactive]);

  return {
    templates,
    loading,
    error
  };
}

